import React, { memo } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag } from '@fortawesome/free-solid-svg-icons/faFlag';
import { faMessage } from '@fortawesome/free-solid-svg-icons/faMessage';
import { faUserSecret } from '@fortawesome/free-solid-svg-icons/faUserSecret';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import FlagItemModalContainer from 'buyside/components/container/FlagItemModalContainer';
import Link from 'buyside/components/presentation/Link/link';
import MessageButton from 'buyside/components/presentation/MessageModal/messageButton';
import ProfileImage from 'buyside/components/presentation/ProfileImage/profileImage';
import { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';

import { employerProfileUrl, userUrl } from 'buyside/util/urlHelper';

import { ChatMessageFragment } from 'interfaces/graphql-typings';

import UpArrowIcon from 'buyside/svgs/up_arrow.svg';
import DownArrowIcon from 'buyside/svgs/down_arrow.svg';

import styles from './chatMessagesStyles.scss';

const ChatMessage: React.FC<{
  belongsToCurrentUser: boolean;
  chatMessage: ChatMessageFragment;
  hideUserInfo: boolean;
}> = memo(({ belongsToCurrentUser, chatMessage, hideUserInfo }) => {
  const { user, anonymous, direction, participantName } = chatMessage;
  const { id: userId, mostRecentEmployer, profileCity, profileImageUrl } = user || {};

  let userDisplayName;
  let userEmployerName;

  if (!user) {
    userDisplayName = <strong>Former User</strong>;
  } else if (anonymous) {
    userDisplayName = <strong>{participantName}</strong>;

    if (mostRecentEmployer?.currentEmployer && mostRecentEmployer.employer) {
      userEmployerName = (
        <AnonEmployerAssociation
          aumRange={mostRecentEmployer.employer.aumRange}
          cityStyles={styles.anonCity}
          employerStyles={styles.anonEmployer}
          fundType={mostRecentEmployer.employer.fundType}
          profileCity={profileCity}
        />
      );
    }
  } else {
    userDisplayName = <Link to={userUrl(userId)}>{participantName}</Link>;

    if (mostRecentEmployer?.currentEmployer && mostRecentEmployer.employer) {
      userEmployerName = mostRecentEmployer.employer.employerProfile ? (
        <Link to={employerProfileUrl(mostRecentEmployer.employer.employerProfile.id)}>
          {mostRecentEmployer.employer.name}
        </Link>
      ) : (
        <span>{mostRecentEmployer.employer.name}</span>
      );
    }
  }

  return (
    <div
      className={classNames(styles.chatMessage, {
        [styles.belongsToCurrentUser]: belongsToCurrentUser,
        [styles.hideUserInfo]: hideUserInfo
      })}
    >
      <div className={styles.chatMessageUser}>
        {anonymous ? (
          <span className={styles.anonProfileImage}>
            <FontAwesomeIcon icon={faUserSecret} />
          </span>
        ) : (
          <ProfileImage source={profileImageUrl} />
        )}
        <div className={styles.userColumn}>
          <div className={styles.userName}>
            {direction ? (
              <span
                id="direction-arrow"
                className={classNames(styles.directionArrow, {
                  [styles.green]: direction === 'long'
                })}
              >
                {direction === 'long' ? <UpArrowIcon /> : <DownArrowIcon />}
              </span>
            ) : null}
            <p>{userDisplayName}</p>
            {user && !belongsToCurrentUser && !anonymous ? (
              <MessageButton
                buttonOptions={{
                  faIcon: faMessage,
                  text: '',
                  type: BUTTON_TYPES.LINK
                }}
                isCurrentEmployer={Boolean(mostRecentEmployer?.currentEmployer)}
                receiverEmployerName={mostRecentEmployer?.employer?.name}
                receiverId={userId}
                receiverName={participantName}
              />
            ) : null}
          </div>
          <div className={styles.userEmployerName}>{userEmployerName}</div>
        </div>
      </div>
      <div className={styles.chatMessageBody}>
        <p>{chatMessage.body}</p>
        <div className={styles.chatMessageActions}>
          <label>{moment(new Date(chatMessage.createdAt)).fromNow()}</label>
          {belongsToCurrentUser ? null : (
            <FlagItemModalContainer
              buttonOptions={{
                className: styles.flagButton,
                faIcon: faFlag,
                type: BUTTON_TYPES.LINK
              }}
              itemId={chatMessage.id}
              type={chatMessage.__typename}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default ChatMessage;
